import { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import type { EditorEditHandle, RevealLineRequest } from '@papan/collab/client'
import { useUndo, useTheme, useCommandScope, useCommandRegistry, useCoverCommandScope, EditorCoverProvider, type Command, type EditorCover } from '@papan/core-ui/ui'
import { Copy, Scissors, ClipboardPaste, Trash2, WandSparkles } from 'lucide-react'
import type { Tab } from './useWorkspace'
import type { OpenFileTargetWire, OpenPdappTargetWire, OpenFileOptsWire } from '@papan/collab/ui'
// The light subpath — the formatter itself (Prettier) loads only when a document is formatted.
import { canFormatFile } from '@papan/collab/ui/format-document'
import { EditorHost } from './EditorHost'
import { useIdentitySwitchEpoch } from '@papan/files/client/queries'

export type { EditorEditHandle }

interface TabContentProps {
  /** Whether the WORKSPACE holding this tab is the deck currently on screen.
   *  Hidden decks stay mounted, so `isActive` alone (the active TAB within a
   *  workspace) is true in every one of them at once. */
  deckActive?: boolean
  tab: Tab
  tenantId: string
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  saveRef?: React.MutableRefObject<(() => void) | null>
  discardRef?: React.MutableRefObject<(() => void) | null>
  /** Shared editor view mode (preview/source) for text editors */
  viewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
  /** Shared scroll position map (keyed by fileId) */
  scrollRef?: Record<string, number>
  /** Generic view sync state */
  viewSync?: Record<string, Record<string, unknown>>
  /** Increments when previewer closes — triggers live editor scroll re-sync */
  scrollSyncTrigger?: number
  /** Export a generated blob to the drive (used by editors with PDF export etc.) */
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  /** Sandboxed user HTML can post `papan:openFile` to navigate the workspace
   *  to another file. The workspace provides this handler; TabContent
   *  passes it through to the editor (currently consumed by HtmlEditor /
   *  HtmlViewer). Replaces the prior global `window` message listener so
   *  the routing stays scoped to the iframe → editor → workspace chain. */
  onOpenFile?: (path: string) => void
  /** Typed SDK `openFileBy*` target relayed from sandboxed HTML. Carries the
   *  validated wire shape (relativePath / drivePath / fileId) plus the relay
   *  context (`appFolderId` = the app's own folder, host-supplied — never
   *  iframe-trusted — and the advisory `opts`). Threaded to HtmlEditor /
   *  HtmlViewer alongside `onOpenFile`. */
  onOpenFileTarget?: (
    target: OpenFileTargetWire,
    ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
  ) => void
  /** #788 — the app asked to LAUNCH another PDApp (`openPdappByFolderId`).
   *  No `appFolderId`: the target names itself by id. */
  onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void
  /** When set, editors that support it (HtmlEditor) show a "Version history"
   *  tool. SingleFileView wires this to its version panel; the workspace omits
   *  it. */
  onShowHistory?: () => void
  historyActive?: boolean
  /** Close this tab. Offered on the "no longer available" panel (#630) so a
   *  tab left on a removed file has an obvious way out. Optional: without it
   *  the panel still renders, minus the button. */
  onCloseTab?: () => void
  /**
   * A version preview is covering this editor.
   *
   * The overlay is `absolute inset-0` over this component, which stays mounted
   * and stays editable — so without knowing, it goes on owning the keyboard
   * while the user reads an old version. Supplied by WorkspaceView from the
   * overlays' own render conditions.
   */
  versionPreviewOpen?: boolean
  /**
   * #930 — the editor reports that it covers itself (the conflict view's "Saved version" panel hides the live editor
   * behind a read-only copy), or stops. The workspace refuses Save for this tab while it is up — see EditorCover.
   */
  onCoverChange?: (cover: EditorCover | null) => void
  /** #249: a line to show (a chat link `file:42`); text editors scroll to it and flash it. */
  revealLine?: RevealLineRequest
}

// #942 — which editor opens the tab, read-only, the size limit, the remount key
// and the dirty reset all live in EditorHost (shared with `/file/:id`). This
// component keeps the TAB's chrome: undo registration, edit commands, focus.

export const TabContent = forwardRef<EditorEditHandle, TabContentProps>(function TabContent({ tab, tenantId, isActive, deckActive = true, onDirtyChange, onConflictChange, saveRef, discardRef, viewMode, onViewModeChange, scrollRef, viewSync, scrollSyncTrigger, onExportToDrive, onOpenFile, onOpenFileTarget, onOpenPdappTarget, onShowHistory, historyActive, onCloseTab, versionPreviewOpen, onCoverChange, revealLine }, ref) {
  // #1210: an org-email switch reloads the open file as the identity now active (`useIdentitySwitchEpoch`)
  const identityEpoch = useIdentitySwitchEpoch()
  const editorRef = useRef<EditorEditHandle>(null)
  const undo = useUndo()
  // Destructured, and ONLY the setter goes in the dependency arrays below.
  // The context value is a fresh object on every provider render, so an
  // effect keyed on `undo` re-runs constantly — here that meant clicking the
  // explorer set the region to 'chrome' and the very next render set it
  // straight back to 'editor', so the drive never got its undo back.
  const { setUndoRegion } = undo
  // On screen AND the active tab. Either alone is not enough: a hidden
  // workspace still has an active tab, and a visible workspace has inactive
  // ones.
  const owns = isActive && deckActive
  // Read settings from the ThemeProvider context — calling useSettings() here
  // would spin up its own state machine + fetch the settings.json blob, and
  // because every open tab renders a TabContent we'd re-fetch it once per tab.
  const { settings } = useTheme()

  // An access change re-reads this tab's permission app-wide (`FileListCacheSync`, one batched
  // request for every open file), and its detail when that answer changed — so a tab whose
  // access is RE-GRANTED comes back from the "no longer available" panel (#630) by itself
  // (`readPermissionQuery`, #1259). It used to re-read both here, per tab, per change.

  // Only pass registerEditor when this tab is active — and NOT while a version
  // preview covers it. The overlay leaves this editor mounted and editable, and
  // the undo region is already pinned to 'editor', so Ctrl+Z while reading an
  // old version reverted a step in the LIVE file with the user looking at a
  // page that did not change. `SingleFileView` has guarded this since #830;
  // this entry point was missed, and a comment there wrongly said otherwise.
  const registerUndoRedo = owns && !versionPreviewOpen ? undo.registerEditor : undefined

  // …but SOMETHING has to claim the keystroke while the preview is up, or it
  // leaves the tab and reaches the drive's file history — Ctrl+Z over an old
  // version would restore a file deleted minutes ago. So register a decliner:
  // `editable: true` with `canUndo: false` is what UndoProvider reads as
  // "nobody", which is the honest answer for a read-only view.
  //
  // BOTH halves, and the withhold is the load-bearing one. `registerEditor` is
  // last-write-wins; React flushes a child's effects before its parent's, and
  // the editor is a DESCENDANT of this component, so at the moment the preview
  // opens the decliner here is the last write and holds the keystroke on its
  // own. It does not STAY last. Any editor that re-registers afterwards
  // overtakes it, and `useYTextUndo`'s registration effect does re-run: on a new
  // UndoManager when the room changes or the Y.Doc is replaced (an epoch
  // mismatch or a 4001 close — an ordinary reconnect keeps the same doc), and on
  // `suspended` flipping, which a conflict or a reload does. From the first such
  // re-registration onward, withholding is the only half left.
  //
  // The dep names the CALLBACK, not `undo`: the context value is a fresh object
  // every provider render (see the note at the top of this file), so keying on
  // it loops — this effect's body and cleanup both set provider state, which
  // re-renders the provider, which re-runs the effect.
  const registerUndoEditor = undo.registerEditor
  useEffect(() => {
    if (!versionPreviewOpen || !owns) return
    return registerUndoEditor({
      editable: true,
      undo: async () => false,
      redo: async () => false,
      onStateChange: (cb) => { cb({ canUndo: false, canRedo: false }); return () => {} },
    })
  }, [versionPreviewOpen, owns, registerUndoEditor])

  // Relay editor ref to parent
  useImperativeHandle(ref, () => ({
    copy: () => editorRef.current?.copy(),
    cut: () => editorRef.current?.cut(),
    paste: () => editorRef.current?.paste(),
    delete: () => editorRef.current?.delete(),
    getSelection: () => editorRef.current?.getSelection?.() ?? null,
    focusEditor: () => editorRef.current?.focusEditor?.(),
    format: () => editorRef.current?.format?.(),
  }), [])

  // Register editor commands — only the active tab registers under 'editor' scope.
  // Inactive tabs use a unique scope ID that won't be in any scope chain.
  // Default editor commands — disabled by default. Specific editors (DB grid, etc.)
  // override these with enabled commands via their own scope.
  // Editors that support native browser copy/paste (text editors, Collabora)
  // don't need these — the browser handles Cmd+C/V natively.
  const editorCommands = useMemo((): Command[] => [
    { id: 'edit.copy', label: 'Copy', icon: Copy, shortcut: { key: 'c', mod: true }, execute: () => editorRef.current?.copy(), enabled: false, menuGroup: 'edit', menuOrder: 10 },
    { id: 'edit.cut', label: 'Cut', icon: Scissors, shortcut: { key: 'x', mod: true }, execute: () => editorRef.current?.cut(), enabled: false, menuGroup: 'edit', menuOrder: 11 },
    { id: 'edit.paste', label: 'Paste', icon: ClipboardPaste, shortcut: { key: 'v', mod: true }, execute: () => editorRef.current?.paste(), enabled: false, menuGroup: 'edit', menuOrder: 12, separator: 'after' },
    { id: 'edit.delete', label: 'Delete', icon: Trash2, shortcut: { key: 'Backspace' }, execute: () => editorRef.current?.delete(), enabled: false, menuGroup: 'edit', menuOrder: 50, danger: true },
    // The editor's own keymap answers Shift+Alt+F while it has focus; this is the menu entry (and the
    // shortcut from elsewhere in the workspace). Read-only, it does nothing.
    { id: 'edit.format', label: 'Format Document', icon: WandSparkles, shortcut: { key: 'f', shift: true, alt: true }, execute: () => editorRef.current?.format?.(), enabled: canFormatFile(tab.title), menuGroup: 'edit', menuOrder: 40, separator: 'before' },
  ], [tab.title])
  const editorScopeId = isActive ? 'editor' : `editor:inactive:${tab.id}`
  useCommandScope(editorScopeId, 'workspace', editorCommands)

  // #929/#930 — while anything covers this editor (the workspace's version preview over the ACTIVE tab, or the
  // editor's own conflict "Saved version" panel), its scope is COVERED: every command the editor registered under it
  // (Delete Rows, Copy cells…) resolves disabled, in the menus and for shortcuts. See EditorCover.
  const [editorCover, setEditorCover] = useState<EditorCover | null>(null)
  const onCoverChangeRef = useRef(onCoverChange)
  onCoverChangeRef.current = onCoverChange
  const handleCoverChange = useCallback((cover: EditorCover | null) => {
    setEditorCover(cover)
    onCoverChangeRef.current?.(cover)
  }, [])
  const covered = (!!versionPreviewOpen && isActive) || editorCover !== null
  useCoverCommandScope(editorScopeId, covered)

  // Set active scope when this tab is active — no click needed.
  // Also re-set on mousedown for when user switches between explorer and editor.
  const cmdRegistry = useCommandRegistry()
  const [editorEl, setEditorEl] = useState<HTMLDivElement | null>(null)
  const editorFocusRef = useCallback((el: HTMLDivElement | null) => setEditorEl(el), [])
  // Command scope: keyed on `isActive` ONLY, as it always was. Re-keying it
  // on `owns` made it re-fire on every deck switch, and each one forced the
  // scope back to 'editor' — so a round trip through the drive silently took
  // the keyboard away from the workspace's own file tree until the user
  // clicked it again. Only the undo region needed the new key.
  useEffect(() => {
    if (isActive) cmdRegistry.setActiveScope('editor')
  }, [isActive, cmdRegistry])

  // Undo region: keyed on `owns`, so coming BACK from the drive — which
  // claims the chrome region while it is up — hands the keystroke to the
  // editor again. See UndoProvider's resolveUndoTarget: without this the
  // keystroke keeps answering for whichever pane was clicked last, which is
  // how Ctrl+Z in an editor ended up reversing a file operation in the drive.
  useEffect(() => {
    if (owns) setUndoRegion('editor')
  }, [owns, setUndoRegion])
  useEffect(() => {
    if (!editorEl || !owns) return
    const handler = () => {
      cmdRegistry.setActiveScope('editor')
      setUndoRegion('editor')
    }
    editorEl.addEventListener('mousedown', handler, true)
    return () => editorEl.removeEventListener('mousedown', handler, true)
  }, [editorEl, owns, cmdRegistry, setUndoRegion])

  return (
    <EditorCoverProvider onChange={handleCoverChange} covered={covered}>
    <EditorHost
      key={`${tab.id}:${tab.restoredSeq ?? 0}:${identityEpoch}`}
      ref={editorRef}
      fileId={tab.fileId}
      fileName={tab.title}
      fileType={tab.type}
      tenantId={tenantId}
      hostKey={tab.id}
      preferredEditorId={settings.preferredEditors?.[tab.type]}
      isActive={isActive}
      wrapperRef={editorFocusRef}
      onDirtyChange={onDirtyChange}
      onConflictChange={onConflictChange}
      saveRef={saveRef}
      discardRef={discardRef}
      registerUndoRedo={registerUndoRedo}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      scrollRef={scrollRef}
      viewSync={viewSync}
      scrollSyncTrigger={scrollSyncTrigger}
      onExportToDrive={onExportToDrive}
      onOpenFile={onOpenFile}
      onOpenFileTarget={onOpenFileTarget}
      onOpenPdappTarget={onOpenPdappTarget}
      revealLine={revealLine}
      onShowHistory={onShowHistory}
      historyActive={historyActive}
      onClose={onCloseTab}
      deleted={tab.deleted}
    />
    </EditorCoverProvider>
  )
})
